import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FileText, CheckCircle, AlertTriangle, TrendingUp,
    Clock, BarChart3, ArrowUpRight, Calendar, Sparkles, Shield,
    ChevronRight, Hourglass, Send, Edit, Eye, Plus, Building2, ExternalLink, AlertCircle
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Loading, ProgressBar } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import { StatusLaporan } from '../../types';

// ========== CONSTANTS ==========
const MAX_ITEMS_DASHBOARD = 5;
const MAX_DEADLINES = 3;

interface KpiData {
    total_laporan: number;
    draft: number;
    diajukan: number;
    dalam_proses: number;
    menunggu_konfirmasi: number;
    selesai: number;
    ditolak: number;
}

interface LaporanItem {
    id: number;
    kode: string;
    judul: string;
    status: string;
    tanggal_pengajuan: string;
    tingkat_risiko: string;
}

interface TindakLanjutItem {
    id: number;
    laporan_masalah_id: number;
    laporan_masalah?: { id: number; kode: string; judul: string; status: string };
    laporanMasalah?: { id: number; kode: string; judul: string; status: string };
    pic_user_id: number;
    status: string;
    progress_percentage: number;
    target_date?: string;
    target?: string;
    is_pic_lama?: boolean;
    alih_tanggung_jawab?: {
        pic_lama_id: number;
        pic_baru_id: number;
        tanggal: string;
        alasan: string;
    };
    kondisi_selesai?: string;
}

// ========== SUB-COMPONENTS ==========

const KpiCard: React.FC<{
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    value: number | string;
    valueColor?: string;
    subtitle?: string;
}> = ({ icon, iconBg, label, value, valueColor = 'text-neutral-text-primary', subtitle }) => (
    <Card padding="sm" className="relative overflow-hidden group">
        <div className="flex items-start justify-between mb-2">
            <div className={`p-2 rounded-xl ${iconBg}`}>
                {icon}
            </div>
        </div>
        <div>
            <p className={`text-xl font-black ${valueColor} tracking-tight`}>{value}</p>
            <p className="text-[10px] font-bold text-neutral-text-secondary uppercase tracking-wider">{label}</p>
            {subtitle && <p className="text-[9px] text-neutral-border-dark mt-0.5">{subtitle}</p>}
        </div>
    </Card>
);

const SectionHeader: React.FC<{
    icon: React.ReactNode;
    iconColor: string;
    title: string;
    count: number;
    total: number;
    linkTo?: string;
}> = ({ icon, iconColor, title, count, total, linkTo }) => (
    <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
            <div className={`p-1 rounded ${iconColor} text-white`}>
                {icon}
            </div>
            <h3 className="text-sm font-bold text-neutral-text-primary capitalize">{title}</h3>
            {total > 0 && (
                <span className="inline-flex items-center font-semibold rounded-full bg-neutral-bg text-[10px] px-2 py-0.5">
                    {count < total ? `${count}/${total}` : total}
                </span>
            )}
        </div>
        {linkTo && (
            <Link to={linkTo} className="text-[10px] font-bold text-teal-main hover:opacity-80 flex items-center gap-0.5">
                Lihat Semua <ChevronRight className="w-2.5 h-2.5" />
            </Link>
        )}
    </div>
);

const TindakLanjutCard: React.FC<{
    item: TindakLanjutItem;
    type: string;
    compact?: boolean;
    isBaruDialihkan?: boolean;
}> = ({ item, type, compact, isBaruDialihkan }) => {
    const judul = item.laporan_masalah?.judul || item.laporanMasalah?.judul || 'Laporan';
    const kode = item.laporan_masalah?.kode || item.laporanMasalah?.kode || '-';

    return (
        <Link to={`/tindak-lanjut/${item.id}`} className="block group">
            <div className={`p-3 rounded-xl border border-neutral-border bg-white hover:border-teal-main/30 hover:shadow-sm transition-all ${compact ? 'py-2.5' : ''}`}>
                <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-teal-main font-bold">{kode}</span>
                        {isBaruDialihkan && (
                            <span className="inline-flex items-center font-semibold rounded-full text-[8px] animate-pulse bg-yellow-100 text-yellow-700 px-1.5 py-0.5">Baru</span>
                        )}
                    </div>
                    <span className={`text-[9px] font-bold uppercase ${type === 'dialihkan' ? 'text-warning' :
                            type === 'menunggu_konfirmasi' ? 'text-purple-600' :
                                'text-teal-main'
                        }`}>
                        {type === 'dialihkan' ? 'Dilimpahkan' : type === 'menunggu_konfirmasi' ? 'Validasi' : 'Aktif'}
                    </span>
                </div>
                <h4 className="text-xs font-semibold text-neutral-text-primary line-clamp-1 mb-2 group-hover:text-teal-main transition-colors">
                    {judul}
                </h4>
                <div className="space-y-1.5">
                    <div className="flex justify-between text-[9px]">
                        <span className="text-neutral-text-secondary">Progress</span>
                        <span className="font-bold text-teal-main">{item.progress_percentage}%</span>
                    </div>
                    <ProgressBar value={item.progress_percentage} size="sm" color="teal" />
                </div>
            </div>
        </Link>
    );
};

const DeadlineCard: React.FC<{ item: TindakLanjutItem; daysRemaining: number }> = ({ item, daysRemaining }) => {
    const judul = item.laporan_masalah?.judul || item.laporanMasalah?.judul || 'Laporan';
    const isOverdue = daysRemaining < 0;

    return (
        <Link to={`/tindak-lanjut/${item.id}`} className="block group">
            <div className={`p-2.5 rounded-xl border bg-white transition-all ${isOverdue ? 'border-danger/20 hover:border-danger/40' : 'border-neutral-border hover:border-warning/40'
                }`}>
                <div className="flex items-center gap-2">
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex flex-col items-center justify-center ${isOverdue ? 'bg-danger text-white' : 'bg-warning/10 text-warning'
                        }`}>
                        <span className="text-[10px] font-black leading-none">{Math.abs(daysRemaining)}</span>
                        <span className="text-[7px] font-bold uppercase">{isOverdue ? 'Lwt' : 'Hari'}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-[11px] font-bold text-neutral-text-primary truncate mb-0.5">{judul}</h4>
                        <div className="flex justify-between items-center">
                            <span className={`text-[9px] font-medium ${isOverdue ? 'text-danger' : 'text-neutral-text-secondary'}`}>
                                {isOverdue ? 'Terlambat' : 'Sisa waktu'}
                            </span>
                            <span className="text-[10px] font-bold text-neutral-text-primary">
                                {item.progress_percentage || 0}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
    <div className="text-center py-6 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
        <p className="text-[10px] text-neutral-text-secondary italic">{message}</p>
    </div>
);

// ========== MAIN COMPONENT ==========

const DashboardPelapor: React.FC = () => {
    const { user } = useAuthStore();
    const prodi = user?.unit?.name || user?.role?.display_name || 'Unit Saya';
    const currentUserId = user?.id;

    // Loading & Data states
    const [loading, setLoading] = useState(true);
    const [kpiData, setKpiData] = useState<KpiData>({
        total_laporan: 0,
        draft: 0,
        diajukan: 0,
        dalam_proses: 0,
        menunggu_konfirmasi: 0,
        selesai: 0,
        ditolak: 0,
    });
    const [myLaporan, setMyLaporan] = useState<LaporanItem[]>([]);
    const [myTindakLanjut, setMyTindakLanjut] = useState<TindakLanjutItem[]>([]);

    // ========== FETCH DATA ==========
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [dashRes, laporanRes, tindakLanjutRes] = await Promise.all([
                    api.get('/dashboard'),
                    api.get('/laporan', { params: { per_page: 10, scope: 'pelapor' } }),
                    api.get('/monitoring/tindak-lanjut-progress', { params: { per_page: 100 } }),
                ]);

                // Parse dashboard data
                const d = dashRes.data?.data || dashRes.data;
                if (d) {
                    setKpiData({
                        total_laporan: d.total_laporan || 0,
                        draft: d.laporan_per_status?.draf || 0,
                        diajukan: d.laporan_per_status?.diajukan || 0,
                        dalam_proses:
                            (d.laporan_per_status?.validasi_l1 || 0) +
                            (d.laporan_per_status?.validasi_l2 || 0) +
                            (d.laporan_per_status?.antrean_rapim || 0) +
                            (d.laporan_per_status?.diputuskan || 0) +
                            (d.laporan_per_status?.berlangsung || 0),
                        menunggu_konfirmasi:
                            d.laporan_per_status?.menunggu_konfirmasi || 0,
                        selesai:
                            (d.laporan_per_status?.selesai || 0) +
                            (d.laporan_per_status?.diarsipkan || 0),
                        ditolak:
                            (d.laporan_per_status?.ditolak_l1 || 0) +
                            (d.laporan_per_status?.ditolak_l2 || 0),
                    });
                }

                // Parse laporan data
                const lapData = laporanRes.data?.data || laporanRes.data;
                if (Array.isArray(lapData)) {
                    setMyLaporan(lapData);
                } else if (lapData?.data && Array.isArray(lapData.data)) {
                    setMyLaporan(lapData.data);
                }

                // Parse tindak lanjut data
                const tlData = tindakLanjutRes.data?.data || tindakLanjutRes.data;
                if (Array.isArray(tlData)) {
                    setMyTindakLanjut(tlData);
                } else if (tlData?.data && Array.isArray(tlData.data)) {
                    setMyTindakLanjut(tlData.data);
                }
            } catch (err) {
                console.error('Failed to load dashboard:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // ========== HELPER FUNCTIONS ==========

    const isDialihkan = (item: TindakLanjutItem): boolean => {
        if (!currentUserId) return false;
        const isPicLama = item.alih_tanggung_jawab?.pic_lama_id === currentUserId;
        const isBukanPicAktif = item.pic_user_id !== currentUserId;
        return isPicLama || (item.is_pic_lama === true && isBukanPicAktif);
    };

    const isBaruDialihkan = (item: TindakLanjutItem): boolean => {
        if (!currentUserId) return false;
        if (item.alih_tanggung_jawab?.pic_baru_id !== currentUserId) return false;
        if (!item.alih_tanggung_jawab?.tanggal) return false;
        const alihDate = new Date(item.alih_tanggung_jawab.tanggal).getTime();
        const diffDays = (Date.now() - alihDate) / (1000 * 60 * 60 * 24);
        return diffDays <= 3;
    };

    const isMenungguKonfirmasi = (item: TindakLanjutItem): boolean => {
        if (item.kondisi_selesai === 'selesai_final') return false;
        if (item.kondisi_selesai === 'menunggu_konfirmasi') return true;
        const laporanStatus = (
            item.laporan_masalah?.status ||
            item.laporanMasalah?.status ||
            ''
        ).toLowerCase();
        const statusTindakLanjut = (item.status || '').toLowerCase();
        const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);
        return (
            statusTindakLanjut === 'validasi' ||
            (statusTindakLanjut === 'selesai' &&
                item.progress_percentage >= 100 &&
                !isLaporanFinal)
        );
    };

    const isSelesaiFinal = (item: TindakLanjutItem): boolean => {
        if (item.kondisi_selesai === 'selesai_final') return true;
        const laporanStatus = (
            item.laporan_masalah?.status ||
            item.laporanMasalah?.status ||
            ''
        ).toLowerCase();
        const statusTindakLanjut = (item.status || '').toLowerCase();
        return statusTindakLanjut === 'selesai' && ['selesai', 'diarsipkan'].includes(laporanStatus);
    };

    const getDaysRemaining = (item: TindakLanjutItem): number => {
        const targetDate = item.target_date || item.target;
        if (!targetDate) return 999;
        const target = new Date(targetDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        target.setHours(0, 0, 0, 0);
        return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    };

    const getStatusAction = (status: string) => {
        const s = status.toLowerCase();
        switch (s) {
            case 'draf':
                return { label: 'Edit', icon: <Edit className="w-3 h-3" /> };
            case 'ditolak_l1':
            case 'ditolak_l2':
                return { label: 'Perbaiki', icon: <Edit className="w-3 h-3" /> };
            default:
                return { label: 'Detail', icon: <Eye className="w-3 h-3" /> };
        }
    };

    // ========== FILTER & SORT DATA ==========

    const relevantTindakLanjut = myTindakLanjut.filter((item) => {
        if (!currentUserId) return false;
        return item.pic_user_id === currentUserId || isDialihkan(item);
    });

    const tindakLanjutDialihkan = relevantTindakLanjut.filter((item) =>
        isDialihkan(item)
    );

    const tindakLanjutMenungguKonfirmasi = relevantTindakLanjut.filter(
        (item) => !isDialihkan(item) && isMenungguKonfirmasi(item)
    );

    const tindakLanjutSelesai = relevantTindakLanjut.filter(
        (item) => !isDialihkan(item) && isSelesaiFinal(item)
    );

    const tindakLanjutAktif = relevantTindakLanjut
        .filter(
            (item) =>
                !isDialihkan(item) &&
                !isMenungguKonfirmasi(item) &&
                !isSelesaiFinal(item) &&
                item.status !== 'dibatalkan' &&
                item.status !== 'batal'
        )
        .sort((a, b) => {
            const daysA = getDaysRemaining(a);
            const daysB = getDaysRemaining(b);
            if (daysA < 0 && daysB >= 0) return -1;
            if (daysB < 0 && daysA >= 0) return 1;
            if (daysA !== daysB) return daysA - daysB;
            return (a.progress_percentage || 0) - (b.progress_percentage || 0);
        });

    // Stats
    const overdueCount = tindakLanjutAktif.filter(
        (item) => getDaysRemaining(item) < 0
    ).length;

    const urgentDeadlines = tindakLanjutAktif
        .filter((item) => getDaysRemaining(item) <= 7)
        .slice(0, MAX_DEADLINES);

    const semuaTindakLanjutUntukProgress = [
        ...tindakLanjutAktif,
        ...tindakLanjutMenungguKonfirmasi,
        ...tindakLanjutSelesai,
    ];
    const avgProgress = semuaTindakLanjutUntukProgress.length > 0
        ? Math.round(
            semuaTindakLanjutUntukProgress.reduce(
                (sum, item) => sum + (item.progress_percentage || 0), 0
            ) / semuaTindakLanjutUntukProgress.length
        )
        : 0;

    // Filter Laporan
    const laporanSayaSebagaiPicAktif = new Set(
        relevantTindakLanjut
            .filter((item) => item.pic_user_id === currentUserId && !isDialihkan(item))
            .map(
                (item) =>
                    item.laporan_masalah_id ||
                    item.laporan_masalah?.id ||
                    item.laporanMasalah?.id
            )
            .filter(Boolean)
    );

    const filteredLaporan = myLaporan.filter((lap) => {
        const status = (lap?.status || '').toLowerCase();
        if (
            ['berlangsung', 'selesai', 'diarsipkan'].includes(status) &&
            laporanSayaSebagaiPicAktif.has(lap.id)
        ) {
            return false;
        }
        return true;
    });

    // ========== STATUS FLOW CONFIG ==========
    const statusFlow = [
        { label: 'Draft', count: kpiData.draft, bg: '#9CA3AF', icon: <FileText className="w-3.5 h-3.5" /> },
        { label: 'Diajukan', count: kpiData.diajukan, bg: '#3B82F6', icon: <Send className="w-3.5 h-3.5" /> },
        { label: 'Proses', count: kpiData.dalam_proses, bg: '#F59E0B', icon: <Clock className="w-3.5 h-3.5" /> },
        { label: 'Konfirmasi', count: kpiData.menunggu_konfirmasi, bg: '#8B5CF6', icon: <Hourglass className="w-3.5 h-3.5" /> },
        { label: 'Selesai', count: kpiData.selesai, bg: '#22C55E', icon: <CheckCircle className="w-3.5 h-3.5" /> },
        { label: 'Ditolak', count: kpiData.ditolak, bg: '#EF4444', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    ];

    // ========== LOADING STATE ==========
    if (loading) {
        return <Loading variant="page" message="Memuat data dashboard..." />;
    }

    // Role display
    const roleName = user?.role?.name?.toLowerCase() || '';
    const roleDisplayName =
        user?.role?.display_name ||
        (roleName === 'kabag' ? 'Kepala Bagian' :
            roleName === 'ka_tu' ? 'Kepala Tata Usaha' :
                roleName === 'kalem' ? 'Kepala Lembaga' : 'Kaprodi');

    // ========== RENDER ==========
    return (
        <div className="space-y-5 animate-in">
            <Breadcrumb items={[{ label: 'Dashboard' }, { label: roleDisplayName }]} />

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-black text-teal-dark tracking-tight">
                        Dashboard {roleDisplayName}
                    </h1>
                    <p className="text-xs text-neutral-text-secondary mt-0.5 flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {prodi}
                    </p>
                </div>
                <Link to="/laporan/buat">
                    <button className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-main text-white text-sm font-semibold rounded-lg hover:bg-teal-light shadow-md transition-all">
                        <Plus className="w-4 h-4" /> Buat Laporan
                    </button>
                </Link>
            </div>

            {/* KPI Cards */}
            {(() => {
                const laporanSelesaiLokal = myLaporan.filter(
                    (lap) => lap.status === 'diselesaikan_lokal'
                ).length;

                return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard
                            icon={<FileText className="w-5 h-5 text-blue-600" />}
                            iconBg="bg-blue-100"
                            label="Laporan yang Saya Ajukan"
                            value={filteredLaporan.length}
                            valueColor="text-blue-600"
                        />
                        <KpiCard
                            icon={<TrendingUp className="w-5 h-5 text-teal-600" />}
                            iconBg="bg-teal-100"
                            label="Tindak Lanjut yang Saya Kerjakan"
                            value={tindakLanjutAktif.length}
                            valueColor="text-teal-600"
                            subtitle={`Rata-rata selesai ${avgProgress}%`}
                        />
                        <KpiCard
                            icon={<CheckCircle className="w-5 h-5 text-success" />}
                            iconBg="bg-success/10"
                            label="Selesai"
                            value={kpiData.selesai + laporanSelesaiLokal}
                            valueColor="text-success"
                            subtitle={laporanSelesaiLokal > 0
                                ? `${laporanSelesaiLokal} ditangani pimpinan`
                                : undefined}
                        />
                        <KpiCard
                            icon={<AlertCircle className="w-5 h-5 text-danger" />}
                            iconBg="bg-danger/10"
                            label="Perlu Perhatian"
                            value={overdueCount + kpiData.ditolak}
                            valueColor={overdueCount + kpiData.ditolak > 0 ? 'text-danger' : 'text-neutral-text-secondary'}
                            subtitle={
                                overdueCount > 0 ? `${overdueCount} terlambat` :
                                    kpiData.ditolak > 0 ? `${kpiData.ditolak} perlu diperbaiki` :
                                        'Semua baik'
                            }
                        />
                    </div>
                );
            })()}

            {/* Status Flow - Compact */}
            <Card padding="sm">
                <div className="flex items-center justify-between gap-1 overflow-x-auto">
                    {statusFlow.map((s, i) => (
                        <React.Fragment key={i}>
                            <div className="flex flex-col items-center gap-0.5 min-w-[50px]">
                                <div
                                    className="relative w-8 h-8 rounded-full text-white flex items-center justify-center transition-transform hover:scale-110"
                                    style={{ backgroundColor: s.bg }}
                                >
                                    {s.icon}
                                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-white text-[9px] font-bold text-neutral-text-primary border border-neutral-border flex items-center justify-center">
                                        {s.count}
                                    </span>
                                </div>
                                <span className="text-[9px] font-semibold text-neutral-text-secondary">
                                    {s.label}
                                </span>
                            </div>
                            {i < statusFlow.length - 1 && (
                                <ChevronRight className="w-3 h-3 text-neutral-border shrink-0" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </Card>

            {/* Alerts */}
            {(overdueCount > 0 || kpiData.ditolak > 0 || tindakLanjutMenungguKonfirmasi.length > 0) && (
                <div className="space-y-2">
                    {overdueCount > 0 && (
                        <div className="p-2.5 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-danger shrink-0" />
                            <p className="text-xs font-medium text-danger flex-1">
                                {overdueCount} tindak lanjut terlambat!
                            </p>
                            <Link to="/laporan?scope=pic&status=overdue" className="text-[10px] font-semibold text-danger hover:underline">
                                Lihat →
                            </Link>
                        </div>
                    )}
                    {kpiData.ditolak > 0 && (
                        <div className="p-2.5 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                            <p className="text-xs font-medium text-warning flex-1">
                                {kpiData.ditolak} laporan perlu diperbaiki
                            </p>
                            <Link to="/laporan?scope=pelapor&status=ditolak_l1" className="text-[10px] font-semibold text-warning hover:underline">
                                Lihat →
                            </Link>
                        </div>
                    )}
                    {tindakLanjutMenungguKonfirmasi.length > 0 && (
                        <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-2">
                            <Hourglass className="w-4 h-4 text-purple-600 shrink-0" />
                            <p className="text-xs font-medium text-purple-700 flex-1">
                                {tindakLanjutMenungguKonfirmasi.length} menunggu konfirmasi pimpinan
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Left: Laporan & Tindak Lanjut */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Laporan Saya */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                Laporan Saya
                            </h3>
                            <Link
                                to="/laporan?scope=pelapor"
                                className="text-[10px] font-semibold text-teal-main hover:underline flex items-center gap-1"
                            >
                                Semua ({filteredLaporan.length}) <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                        <div className="space-y-2">
                            {filteredLaporan.length > 0 ? (
                                filteredLaporan.slice(0, 4).map((lap) => {
                                    const action = getStatusAction(lap.status);
                                    return (
                                        <Link
                                            key={lap.id}
                                            to={lap.status === 'draf' ? `/laporan/${lap.id}/edit` : `/laporan/${lap.id}`}
                                        >
                                            <div className="flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-neutral-bg/50 transition-colors">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <span className="text-[10px] font-mono text-teal-main">
                                                            {lap.kode}
                                                        </span>
                                                        <Badge variant="status" status={lap.status as StatusLaporan} />
                                                    </div>
                                                    <p className="text-xs font-medium truncate">{lap.judul}</p>
                                                </div>
                                                <button className="p-1.5 rounded-lg bg-teal-main/10 hover:bg-teal-main/20">
                                                    {action.icon}
                                                </button>
                                            </div>
                                        </Link>
                                    );
                                })
                            ) : (
                                <div className="text-center py-6">
                                    <FileText className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
                                    <p className="text-xs text-neutral-text-secondary">Belum ada laporan</p>
                                    <Link to="/laporan/buat" className="text-[10px] text-teal-main font-semibold hover:underline">
                                        Buat laporan pertama →
                                    </Link>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Tindak Lanjut Saya - COMPACT */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Tindak Lanjut Saya
                            </h3>
                            <Link
                                to="/laporan?scope=pic"
                                className="text-[10px] font-semibold text-teal-main hover:underline flex items-center gap-1"
                            >
                                Lihat Semua <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>

                        <div className="space-y-4">
                            {/* Aktif */}
                            <div>
                                <SectionHeader
                                    icon={<Clock className="w-3 h-3" />}
                                    iconColor="bg-warning"
                                    title="Aktif"
                                    count={Math.min(tindakLanjutAktif.length, MAX_ITEMS_DASHBOARD)}
                                    total={tindakLanjutAktif.length}
                                    linkTo="/laporan?scope=pic&status=berlangsung"
                                />
                                <div className="space-y-2">
                                    {tindakLanjutAktif.length > 0 ? (
                                        tindakLanjutAktif.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`aktif-${item.id}`}
                                                item={item}
                                                type="aktif"
                                                isBaruDialihkan={isBaruDialihkan(item)}
                                                compact
                                            />
                                        ))
                                    ) : (
                                        <EmptyState message="Tidak ada tindak lanjut aktif" />
                                    )}
                                </div>
                            </div>

                            {/* Menunggu Konfirmasi */}
                            {tindakLanjutMenungguKonfirmasi.length > 0 && (
                                <div>
                                    <SectionHeader
                                        icon={<Hourglass className="w-3 h-3" />}
                                        iconColor="bg-purple-500"
                                        title="Menunggu Konfirmasi"
                                        count={Math.min(tindakLanjutMenungguKonfirmasi.length, MAX_ITEMS_DASHBOARD)}
                                        total={tindakLanjutMenungguKonfirmasi.length}
                                        linkTo="/laporan?scope=pic&status=menunggu_konfirmasi"
                                    />
                                    <div className="space-y-2">
                                        {tindakLanjutMenungguKonfirmasi.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`menunggu-${item.id}`}
                                                item={item}
                                                type="menunggu_konfirmasi"
                                                compact
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Selesai */}
                            {tindakLanjutSelesai.length > 0 && (
                                <div>
                                    <SectionHeader
                                        icon={<CheckCircle className="w-3 h-3" />}
                                        iconColor="bg-success"
                                        title="Selesai"
                                        count={Math.min(tindakLanjutSelesai.length, MAX_ITEMS_DASHBOARD)}
                                        total={tindakLanjutSelesai.length}
                                        linkTo="/laporan?scope=pic&status=selesai"
                                    />
                                    <div className="space-y-2">
                                        {tindakLanjutSelesai.slice(0, MAX_ITEMS_DASHBOARD).map((item) => (
                                            <TindakLanjutCard
                                                key={`selesai-${item.id}`}
                                                item={item}
                                                type="selesai"
                                                compact
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Right: Sidebar */}
                <div className="space-y-5">
                    {/* Deadline Terdekat */}
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-teal-dark flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Deadline
                            </h3>
                        </div>
                        <div className="space-y-2">
                            {urgentDeadlines.length > 0 ? (
                                urgentDeadlines.map((item) => (
                                    <DeadlineCard
                                        key={`dl-${item.id}`}
                                        item={item}
                                        daysRemaining={getDaysRemaining(item)}
                                    />
                                ))
                            ) : (
                                <div className="text-center py-4">
                                    <CheckCircle className="w-6 h-6 mx-auto mb-1 text-success" />
                                    <p className="text-[10px] text-neutral-text-secondary">
                                        Tidak ada deadline mendesak
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Progress Summary */}
                    <Card>
                        <h3 className="text-sm font-bold text-teal-dark mb-1 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Ringkasan
                        </h3>
                        <p className="text-[10px] text-neutral-text-secondary leading-tight mb-3">
                            Ringkasan performa Anda sebagai Penanggung Jawab (PIC) Tindak Lanjut perbaikan.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] text-neutral-text-secondary">
                                        Progress Rata-rata
                                    </span>
                                    <span className="text-xs font-bold text-teal-main">
                                        {avgProgress}%
                                    </span>
                                </div>
                                <ProgressBar value={avgProgress} size="sm" color="teal" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-teal-main">
                                        {tindakLanjutAktif.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Aktif">TL Aktif</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-purple-600">
                                        {tindakLanjutMenungguKonfirmasi.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Menunggu Konfirmasi">TL Konfirmasi</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className="text-lg font-black text-success">
                                        {tindakLanjutSelesai.length}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Selesai">TL Selesai</p>
                                </div>
                                <div className="p-2 bg-neutral-bg rounded-lg text-center">
                                    <p className={`text-lg font-black ${overdueCount > 0 ? 'text-danger' : 'text-neutral-text-secondary'}`}>
                                        {overdueCount}
                                    </p>
                                    <p className="text-[9px] text-neutral-text-secondary block truncate" title="Tindak Lanjut Terlambat">TL Terlambat</p>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Quick Actions */}
                    <Card>
                        <h3 className="text-sm font-bold text-teal-dark mb-3 flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            Aksi Cepat
                        </h3>
                        <div className="space-y-2">
                            <Link to="/laporan/buat" className="block">
                                <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-teal-main/5 hover:border-teal-main/20 transition-colors text-left">
                                    <div className="p-1.5 rounded-lg bg-teal-main/10">
                                        <Plus className="w-3.5 h-3.5 text-teal-main" />
                                    </div>
                                    <span className="text-xs font-semibold">Buat Laporan Baru</span>
                                </button>
                            </Link>
                            <Link to="/laporan" className="block">
                                <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-blue-50 hover:border-blue-200 transition-colors text-left">
                                    <div className="p-1.5 rounded-lg bg-blue-100">
                                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                                    </div>
                                    <span className="text-xs font-semibold">Semua Laporan</span>
                                </button>
                            </Link>
                            {tindakLanjutAktif.length > 0 && (
                                <Link to={`/tindak-lanjut/${tindakLanjutAktif[0].id}`} className="block">
                                    <button className="w-full flex items-center gap-2 p-2.5 rounded-xl border border-neutral-border hover:bg-warning/5 hover:border-warning/20 transition-colors text-left">
                                        <div className="p-1.5 rounded-lg bg-warning/10">
                                            <TrendingUp className="w-3.5 h-3.5 text-warning" />
                                        </div>
                                        <span className="text-xs font-semibold">Update Progress</span>
                                    </button>
                                </Link>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DashboardPelapor;
